
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import {
  _AbcButtonComponent,
  _AbcGridComponent,
} from "@barthauer/abc/ui-components";
import { DxDataGridModule, DxTemplateModule } from "devextreme-angular";
import { Column } from "devextreme/ui/data_grid";
import {
  defaultGridOptions,
  defaultLabels,
  DocumentListGridOptions,
  FilterPanelMode,
  GeoDsDocumentViewModel,
  IActionButtonsMenuOutput,
  IContextMenuItems,
  Labels,
} from "../../document.editor.models";

@Component({
    selector: "abc-common-editors-documents-grid",
    templateUrl: "./grid.component.html",
    styleUrls: ["./grid.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    _AbcGridComponent,
    DxDataGridModule,
    _AbcButtonComponent,
    DxTemplateModule
]
})
export class GridComponent implements OnChanges {
  @Input() allowMultipleSelection = false;
  @Input() allowSearch = true;
  @Input() dataSource: GeoDsDocumentViewModel[] = [];
  @Input() labels?: Labels = defaultLabels;
  @Input() height = "100%";
  @Input() actionsContextMenu = false;
  @Input() isContextMenuVisible = false;
  @Input() additionalGridColumns: Column[] = [];
  @Input() gridOptions: DocumentListGridOptions = defaultGridOptions;

  public specificButtonsValue: IContextMenuItems[] = [];

  @Input() set specificButtons(
    val: (
      doc?: GeoDsDocumentViewModel
    ) =>
      | IContextMenuItems[]
      | Array<(doc?: GeoDsDocumentViewModel) => IContextMenuItems[]>
  ) {
    this.specificButtonsValue = Array.isArray(val)
      ? val
        .map((fn) => fn(this.selectedDocument))
        .reduce((acc, items) => acc.concat(items), [])
      : val
        ? val(this.selectedDocument)
        : [];
  }

  get specificButtons(): IContextMenuItems[] {
    return this.specificButtonsValue;
  }

  @Input() selectedDocumentsIds: string[] = [];
  @Output() selectedDocumentsIdsChange = new EventEmitter<string[]>();

  @Output() previewDocument = new EventEmitter<string>();
  @Output() downloadDocument = new EventEmitter<string>();
  @Output() openDeleteDocumentPopup = new EventEmitter<string>();
  @Output() goToDetails = new EventEmitter<string>();
  @Output() openActionButtonsMenu =
    new EventEmitter<IActionButtonsMenuOutput>();
  @Output() rowDblClick = new EventEmitter<GeoDsDocumentViewModel>();

  public selectedDocument!: GeoDsDocumentViewModel;
  public filterBuilderVisible = false;

  public defaultFilterPanelMode = FilterPanelMode.GridBottom;
  public columnsHeader: Column[] = [
    {
      caption: this.labels?.Designation,
      dataField: "Designation",
      alignment: "center",
      dataType: "string",
    },
    {
      caption: this.labels?.FileName,
      dataField: "FileName",
      alignment: "center",
      dataType: "string",
    },
    {
      caption: this.labels?.InsertedBy,
      dataField: "IdRefPersonInsertFullName",
      alignment: "center",
      dataType: "string",
      visible: false,
      showInColumnChooser: true,
    },
    {
      caption: this.labels?.Kind,
      dataField: "Kind",
      alignment: "center",
      dataType: "string",
      visible: false,
      showInColumnChooser: true,
    },
    {
      caption: this.labels?.InsertDate,
      dataField: "InsertDate",
      alignment: "center",
      dataType: "date",
      visible: false,
      showInColumnChooser: true,
      format: "dd.MM.yyyy",
    },
    {
      type: "buttons",
      caption: "",
      showInColumnChooser: false,
      fixed: true,
      fixedPosition: "right",
      cellTemplate: "buttons",
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.additionalGridColumns &&
      changes.additionalGridColumns?.currentValue.length > 0
    ) {
      changes.additionalGridColumns?.currentValue.forEach((newColumn) => {
        const columnIndex = this.columnsHeader.findIndex(
          (column) => column.dataField === newColumn.dataField
        );

        if (columnIndex > -1) {
          this.columnsHeader[columnIndex] = newColumn;
        } else {
          this.columnsHeader.splice(
            this.columnsHeader.length - 1,
            0,
            newColumn
          );
        }
      });
    }

    if (
      changes.gridOptions &&
      Object.keys(changes.gridOptions?.currentValue).length > 0
    ) {
      this.gridOptions = {
        ...Object.assign(defaultGridOptions, changes.gridOptions.currentValue),
      };
    }
  }

  public onRowDblClick(event: { data: GeoDsDocumentViewModel }): void {
    this.rowDblClick.emit(event.data);
  }

  public onOpenActionButtonsMenu(
    event: any,
    doc: GeoDsDocumentViewModel
  ): void {
    this.selectedDocument = doc;
    this.openActionButtonsMenu.emit({
      target: event.event.currentTarget,
      selectedDocument: doc,
    });
  }

  public changeRowSelection(event: { selectedRowKeys: string[] }): void {
    this.selectedDocumentsIds = event.selectedRowKeys;
    this.selectedDocumentsIdsChange.emit(this.selectedDocumentsIds);
  }

  public openFilterBuilder(): void {
    this.filterBuilderVisible = true;
  }
}
